/**
 * ThirdStatsGrid.tsx
 * 
 * Third row of stats cards for Subscription Expires and Feedback List
 */

import React from 'react';
import { Calendar, MessageSquare } from 'lucide-react';
import type { ReactElement } from 'react';

export type ThirdStats = {
  subscriptionExpires: number;
  feedbackList: number;
};

type Props = {
  stats: ThirdStats;
  onCardClick: (key: keyof ThirdStats) => void;
};

export default function ThirdStatsGrid({ stats, onCardClick }: Props): ReactElement {
  const cards: Array<{
    key: keyof ThirdStats;
    title: string;
    value: number;
    icon: ReactElement;
    color: string;
    gradient: string;
  }> = [
    {
      key: 'subscriptionExpires',
      title: 'Subscription Expires',
      value: stats.subscriptionExpires,
      icon: <Calendar className="w-5 h-5" />,
      color: 'text-orange-600',
      gradient: 'from-orange-500 to-amber-500',
    },
    {
      key: 'feedbackList',
      title: 'Feedback List',
      value: stats.feedbackList,
      icon: <MessageSquare className="w-5 h-5" />,
      color: 'text-teal-600',
      gradient: 'from-teal-500 to-cyan-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      {cards.map((card) => (
        <button
          key={card.key}
          onClick={() => onCardClick(card.key)}
          className={`bg-gradient-to-br ${card.gradient} rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] text-left group`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform">
              {card.icon}
            </div>
            <div className="text-3xl font-bold text-white">{card.value}</div>
          </div>
          <div className="text-sm font-semibold text-white/90">{card.title}</div>
        </button>
      ))}
    </div>
  );
}
